import React from "react";
import AnimationRevealPage from "../../../../helpers/AnimationRevealPage.js";
import tw from "twin.macro";
import Header from "../../../../Components/headers/PageHeader";
import MainFeature1 from "../../../../Components/Features/TwoColWithButton.js"; // Reused component
import Features from "../../../../Components/Features/ThreeColumnPages"; // Reused component
import {
  FaPaintBrush,
  FaGlobe,
  FaUsers,
  FaHandshake,
  FaAward,
  FaMapMarkerAlt,
  FaChartLine,
} from "react-icons/fa";

// Styled Components
const Subheading = tw.span`uppercase tracking-wider text-sm`;
const Heading = tw.h1`text-2xl md:text-4xl lg:text-5xl font-bold`;
const Description = tw.p`text-base md:text-lg lg:text-xl`;

// Web UI/UX Design in Dubai Page Component
export const WebUIUXDesignInDubai = () => {
  return (
    <AnimationRevealPage>
      {/* Header Section */}
      <Header from="Web UI/UX Design in Dubai" />

      {/* Hero Section */}
      <MainFeature1
        subheading={<Subheading>Build a Website That Converts</Subheading>}
        heading={
          <Heading>
            Elevate Your Online Presence with{" "}
            <span tw="text-primary-500">
              The Best Web UI/UX Design in Dubai
            </span>
          </Heading>
        }
        description={
          <Description>
            At MilaajBrandset, we specialize in delivering professional web
            UI/UX design services tailored for businesses and individuals across
            **Dubai**. Our team combines creativity, strategy, and innovation to
            help you engage your audience, grow your brand, and achieve
            measurable results through impactful digital experiences.
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // UI/UX designer working
          "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // Team collaboration on design
          "https://images.unsplash.com/photo-1586717791821-3f44a563fa4c?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // Digital interface mockup
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Get Started Today"
        buttonRounded={true}
        textOnLeft={false}
      />

      {/* Key Benefits Section */}
      <Features
        subheading={<Subheading>Why Choose Us?</Subheading>}
        heading="Your Trusted Partner for Web UI/UX Design in Dubai"
        description="We combine creativity, functionality, and innovation to deliver unmatched value."
        cards={[
          {
            icon: <FaPaintBrush color="#ff5464" size={40} />,
            title: "Creative Designs",
            description:
              "Craft visually stunning and intuitive interfaces that captivate your audience.",
          },
          {
            icon: <FaGlobe color="#ff5464" size={40} />,
            title: "Responsive Websites",
            description:
              "Design websites that work seamlessly across all devices and screen sizes.",
          },
          {
            icon: <FaMapMarkerAlt color="#ff5464" size={40} />,
            title: "Localized Expertise",
            description:
              "Understand Dubai's unique market trends and diverse demographics.",
          },
          {
            icon: <FaUsers color="#ff5464" size={40} />,
            title: "User-Centric Approach",
            description:
              "Prioritize usability, accessibility, and engagement for all users.",
          },
          {
            icon: <FaHandshake color="#ff5464" size={40} />,
            title: "Client-Centric Collaboration",
            description:
              "Work closely with you to ensure alignment with your vision and goals.",
          },
          {
            icon: <FaAward color="#ff5464" size={40} />,
            title: "Proven Track Record",
            description:
              "Delivering exceptional results for Dubai clients since [Year].",
          },
        ]}
      />

      {/* Our Process Section */}
      <MainFeature1
        subheading={<Subheading>How We Work</Subheading>}
        heading="Our Streamlined Process for Web UI/UX Design in Dubai"
        description={
          <Description>
            Our Dubai-focused process ensures quality, efficiency, and
            measurable results:
            <ul tw="mt-4 list-disc pl-6">
              <li>
                Discovery & Research: Understand your business, audience, and
                goals.
              </li>
              <li>
                User Analysis: Identify your target demographic and their
                preferences.
              </li>
              <li>
                Wireframing & Prototyping: Create interactive prototypes for
                testing and feedback.
              </li>
              <li>
                Design Execution: Craft visually stunning and intuitive designs
                tailored for your audience.
              </li>
              <li>
                Development Integration: Collaborate with developers to bring
                your designs to life.
              </li>
              <li>
                Optimization & Feedback: Continuously refine designs based on
                user feedback and performance metrics.
              </li>
            </ul>
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // Wireframing/prototyping
          "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // UX research meeting
          "https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // UI design on screen
        ]}
        widths={["60%", "55%", "70%"]}
        heights={["60%", "45%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "right: 30%; top: 10%;",
          "left: 35%; bottom: 20%;",
          "right: 32%; bottom: 10%;",
        ]}
        primaryButtonText="Learn More"
        buttonRounded={true}
        textOnLeft={true}
      />

      {/* Success Stories Section */}
      <Features
        subheading={<Subheading>Success Stories</Subheading>}
        heading="Results That Speak for Themselves"
        description="Here’s how our web UI/UX design services have empowered businesses in Dubai."
        cards={[
          {
            icon: <FaAward color="#ff5464" size={40} />,
            title: "Improved User Engagement",
            description:
              "Helped an e-commerce platform in Dubai increase user retention by 150% through intuitive design.",
          },
          {
            icon: <FaHandshake color="#ff5464" size={40} />,
            title: "Client Satisfaction",
            description:
              "98% client retention rate through consistent excellence and measurable results.",
          },
          {
            icon: <FaChartLine color="#ff5464" size={40} />,
            title: "Performance Boost",
            description:
              "Achieved a 200% increase in conversions for a local retail app using optimized UX strategies.",
          },
        ]}
      />

      {/* Call-to-Action Section */}
      <MainFeature1
        subheading={<Subheading>Ready to Build a Stunning Website?</Subheading>}
        heading="Let’s Create Exceptional Web Experiences Together in Dubai"
        description={
          <Description>
            Partner with Dubai’s leading web UI/UX experts to unlock your
            business’s full potential. Whether you’re launching a website or
            revamping an existing one, we’re here to help.
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // Dubai skyline
          "https://images.unsplash.com/photo-1559028012-481c04fa702d?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // Designer working on digital interface
          "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // Modern office space with designers
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Contact Us Today"
        buttonRounded={true}
        textOnLeft={false}
      />
    </AnimationRevealPage>
  );
};
